import { useCallback, useState } from 'react';

interface ExportSettings {
  acrossBothTabs: boolean;
}

/** The state a seller starts in, and the fallback whenever storage can't be trusted. */
const EXPORT_SETTINGS_DEFAULT: Readonly<ExportSettings> = {
  acrossBothTabs: false,
};

const STORAGE_KEY = 'iwocapay.customersV2.exportSettings';

/**
 * Reads the persisted preference. Any failure — unavailable or corrupt storage,
 * a non-object payload, a non-boolean value — reads as off and never throws.
 */
const readSettings = (): ExportSettings => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return { ...EXPORT_SETTINGS_DEFAULT };
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return { ...EXPORT_SETTINGS_DEFAULT };
    }
    return {
      acrossBothTabs:
        (parsed as Record<string, unknown>).acrossBothTabs === true,
    };
  } catch {
    return { ...EXPORT_SETTINGS_DEFAULT };
  }
};

export function useExportSettings(): {
  settings: ExportSettings;
  setAcrossBothTabs: (value: boolean) => void;
} {
  const [settings, setSettings] = useState<ExportSettings>(readSettings);

  const setAcrossBothTabs = useCallback((value: boolean) => {
    const next: ExportSettings = { acrossBothTabs: value };
    setSettings(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Persisting is best-effort; in-memory state is still updated above.
    }
  }, []);

  return { settings, setAcrossBothTabs };
}
